// src/components/ui/SplitViewOverlay.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const CloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
    <path d="M2 2l12 12M14 2 2 14" strokeLinecap="round"/>
  </svg>
);
const RefreshIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
    <path d="M13.5 8A5.5 5.5 0 1 1 8 2.5c1.8 0 3.4.87 4.4 2.2" strokeLinecap="round"/>
    <path d="M12 2v3h-3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const SwapIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
    <path d="M1 5h10M8 2l3 3-3 3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 11H5m3 3-3-3 3-3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const DragIcon = () => (
  <svg viewBox="0 0 8 24" fill="currentColor" className="w-2 h-6">
    <circle cx="2" cy="6"  r="1.4"/><circle cx="6" cy="6"  r="1.4"/>
    <circle cx="2" cy="12" r="1.4"/><circle cx="6" cy="12" r="1.4"/>
    <circle cx="2" cy="18" r="1.4"/><circle cx="6" cy="18" r="1.4"/>
  </svg>
);

/* ─── PanelBar ───────────────────────────────────────────────────────────── */
function PanelBar({ side, url, onReload, onClose }) {
  return (
    <div className="flex items-center gap-2 px-3 h-9 bg-[#1e1e1e] border-b border-[#2c2c2c] flex-shrink-0 min-w-0">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${side === "left" ? "bg-blue-400" : "bg-emerald-400"}`} />
      <span className="text-xs font-semibold text-[#555] uppercase tracking-widest flex-shrink-0">
        {side}
      </span>
      <span className="text-xs text-[#888] truncate flex-1 min-w-0 font-mono select-text">{url}</span>
      <button
        onClick={onReload}
        title="Reload"
        className="flex-shrink-0 p-1 rounded text-[#555] hover:text-[#ccc] hover:bg-[#2a2a2a] transition-colors"
      >
        <RefreshIcon />
      </button>
      {side === "right" && (
        <button
          onClick={onClose}
          title="Close split view (Esc)"
          className="flex-shrink-0 p-1 rounded text-[#555] hover:text-red-400 hover:bg-[#2a2a2a] transition-colors ml-1"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

/* ─── SplitViewOverlay ───────────────────────────────────────────────────── */
export function SplitViewOverlay() {
  const [active,   setActive]   = useState(false);
  const [leftUrl,  setLeftUrl]  = useState("");
  const [rightUrl, setRightUrl] = useState("");
  const [divider,  setDivider]  = useState(50);   // left panel width %
  const [leftKey,  setLeftKey]  = useState(0);    // bump to reload iframe
  const [rightKey, setRightKey] = useState(0);

  const containerRef = useRef(null);
  const dragging     = useRef(false);

  // Keep stable refs so the __splitView object below always sees latest values
  const leftUrlRef  = useRef(leftUrl);
  const rightUrlRef = useRef(rightUrl);
  useEffect(() => { leftUrlRef.current  = leftUrl;  }, [leftUrl]);
  useEffect(() => { rightUrlRef.current = rightUrl; }, [rightUrl]);

  /* ── Swap helper (used by ContextMenu via __splitView.swap) ────────────── */
  const doSwap = useCallback(() => {
    setLeftUrl(rightUrlRef.current);
    setRightUrl(leftUrlRef.current);
    setLeftKey(k  => k + 1);
    setRightKey(k => k + 1);
  }, []);

  /* ── Close helper ──────────────────────────────────────────────────────── */
  const doClose = useCallback(() => {
    setActive(false);
    setLeftUrl("");
    setRightUrl("");
  }, []);

  /* ── Publish state + actions to window so ContextMenu can reach them ───── */
  useEffect(() => {
    window.__splitView = {
      active,
      leftUrl,
      rightUrl,
      /** Load url into a pane without reopening the overlay */
      openInPane(url, pane /* "left" | "right" */) {
        if (pane === "left")  { setLeftUrl(url);  setLeftKey(k  => k + 1); }
        if (pane === "right") { setRightUrl(url); setRightKey(k => k + 1); }
      },
      /** Swap left ↔ right panes */
      swap: doSwap,
      /** Close the overlay entirely */
      close: doClose,
    };
  }, [active, leftUrl, rightUrl, doSwap, doClose]);

  /* ── Listen for "open-split-view" ─────────────────────────────────────── */
  useEffect(() => {
    const handler = ({ detail }) => {
      const { url, pane = "right" } = detail;

      // Already in split view → just update the target pane, no new overlay
      if (window.__splitView?.active) {
        window.__splitView.openInPane(url, pane);
        return;
      }

      // Not in split view → open fresh split (current page left, url right)
      setLeftUrl(window.location.href);
      setRightUrl(url);
      setDivider(50);
      setLeftKey(0);
      setRightKey(0);
      setActive(true);
    };

    window.addEventListener("open-split-view", handler);
    return () => window.removeEventListener("open-split-view", handler);
  }, []);

  /* ── Listen for "swap-split-panes" (fired by ContextMenu from iframe) ─── */
  useEffect(() => {
    const handler = () => doSwap();
    window.addEventListener("swap-split-panes", handler);
    return () => window.removeEventListener("swap-split-panes", handler);
  }, [doSwap]);

  /* ── Escape closes ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!active) return;
    const h = (e) => { if (e.key === "Escape") doClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [active, doClose]);

  /* ── Draggable divider ───────────────────────────────────────────────── */
  const onMouseMove = useCallback((e) => {
    if (!dragging.current || !containerRef.current) return;
    const { left, width } = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - left) / width) * 100;
    setDivider(Math.min(80, Math.max(20, pct)));
  }, []);

  const stopDrag = useCallback(() => { dragging.current = false; }, []);

  useEffect(() => {
    if (!active) return;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup",   stopDrag);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup",   stopDrag);
    };
  }, [active, onMouseMove, stopDrag]);

  if (!active) return null;

  return createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9990] flex bg-[#111] overflow-hidden"
      style={{ userSelect: dragging.current ? "none" : "auto" }}
    >
      {/* ── Left pane ──────────────────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden" style={{ width: `${divider}%`, minWidth: 0 }}>
        <PanelBar
          side="left"
          url={leftUrl}
          onReload={() => setLeftKey(k => k + 1)}
          onClose={doClose}
        />
        <iframe
          key={`L${leftKey}`}
          src={leftUrl}
          title="Split left"
          className="flex-1 w-full border-0 bg-white"
          style={{ pointerEvents: dragging.current ? "none" : "auto" }}
        />
      </div>

      {/* ── Divider ─────────────────────────────────────────────────────── */}
      <div
        onMouseDown={(e) => { e.preventDefault(); dragging.current = true; }}
        className="relative flex-shrink-0 flex items-center justify-center w-[6px] bg-[#1a1a1a] hover:bg-blue-500 cursor-col-resize group transition-colors"
      >
        <div className="absolute w-5 h-10 bg-[#2a2a2a] group-hover:bg-blue-500 rounded-full flex items-center justify-center shadow-xl text-[#777] group-hover:text-white transition-all">
          <DragIcon />
        </div>
      </div>

      {/* ── Right pane ─────────────────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden" style={{ width: `${100 - divider - 0.04}%`, minWidth: 0 }}>
        <PanelBar
          side="right"
          url={rightUrl}
          onReload={() => setRightKey(k => k + 1)}
          onClose={doClose}
        />
        <iframe
          key={`R${rightKey}`}
          src={rightUrl}
          title="Split right"
          className="flex-1 w-full border-0 bg-white"
          style={{ pointerEvents: dragging.current ? "none" : "auto" }}
        />
      </div>

      {/* ── Floating swap / close toolbar ───────────────────────────────── */}
      <div
        className="absolute top-2 z-10 flex items-center gap-1 bg-[#1e1e1e] border border-[#3a3a3a] rounded-lg px-2 py-1 shadow-2xl"
        style={{ left: `calc(${divider}% - 44px)` }}
      >
        <button
          onClick={doSwap}
          title="Swap panes"
          className="p-1 rounded text-[#888] hover:text-white hover:bg-[#333] transition-colors"
        >
          <SwapIcon />
        </button>
        <div className="w-px h-4 bg-[#3a3a3a]" />
        <button
          onClick={doClose}
          title="Exit split view (Esc)"
          className="p-1 rounded text-[#888] hover:text-red-400 hover:bg-[#333] transition-colors"
        >
          <CloseIcon />
        </button>
      </div>
    </div>,
    document.body
  );
}